import React, { useState } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import Localization from "aldoo-ra/Localization"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { updateSubscriptionQuantity } from "aldoo-ra/Store/api"
import { MinusIcon, PlusIcon, UserGroupIcon } from "@heroicons/react/24/outline"

/**
 * Seat purchase control for subscription-based (per-seat) licenses.
 * Owner picks a new seat count; confirming shows the price change and
 * charges immediately (prorated) on increase / applies next period on
 * decrease. License seats resize automatically server-side.
 */
export default function SeatManagement({ license, onChanged }) {
  const texts = TextBundle({ id: "activate_license" })
  const { t } = Localization()
  const subscription = license.subscription
  const [target, setTarget] = useState(null)
  const [saving, setSaving] = useState(false)

  // Only for the owner of an active per-seat subscription license
  if (!license.isOwner || !subscription || subscription.status !== "active") return null

  const currentSeats = subscription.quantity || 1
  const total = subscription.price_model?.amount || 0
  const currency = subscription.price_model?.currency || "USD"
  const unit = currentSeats > 0 ? total / currentSeats : total
  const durationDays = subscription.price_model?.duration || 30
  const isAnnual = durationDays >= 360
  const periodLabel = isAnnual ? texts?.per_year : texts?.per_month
  const perMonthUnit = isAnnual ? unit / 12 : unit

  const seats = target ?? currentSeats
  const newTotal = Math.round(unit * seats * 100) / 100
  const dirty = seats !== currentSeats

  const applyChange = () => {
    const increasing = seats > currentSeats
    showPromptDialog({
      title: texts?.change_seats_title,
      message: increasing
        ? t(texts?.increase_seats_message, { currentSeats, seats, newTotal, periodLabel })
        : t(texts?.decrease_seats_message, { currentSeats, seats, newTotal, periodLabel }),
      onConfirm: async () => {
        setSaving(true)
        try {
          const result = await updateSubscriptionQuantity({
            subscriptionId: subscription._id,
            quantity: seats,
          })
          if (result?.error) throw new Error(result.message || result.error)
          showNotification({
            message: result.effective === "immediate"
              ? texts?.seats_increased_notification
              : texts?.seats_decreased_notification,
            className: "bg-primary text-white",
          })
          setTarget(null)
          onChanged?.()
        } catch (error) {
          showNotification({
            message: error.message || texts?.seats_update_error,
            className: "bg-error text-white",
          })
        } finally {
          setSaving(false)
        }
      },
    })
  }

  return (
    <div className="mt-6 bg-paper border border-bordered rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-1">
        <UserGroupIcon className="w-5 h-5 text-primary" />
        <Typography 
          variant="body1_accent" 
          className="my-0"
        >
          {texts?.seats_management_title}
        </Typography>
      </div>
      <Typography variant="txt1_dim" className="block mb-4">
        {t(texts?.seats_management_subtitle, {
          perMonthUnit: Math.round(perMonthUnit * 100) / 100,
          annualNote: isAnnual ? ", billed annually" : ""
        })}
      </Typography>

      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            disabled={saving || seats <= 1}
            onClick={() => setTarget(Math.max(1, seats - 1))}
            title={texts?.remove_seat}
          >
            <MinusIcon className="w-4 h-4" />
          </Button>

          <span className="min-w-[90px] text-center font-semibold text-text">
            {seats} {seats === 1 ? texts?.seat_singular : texts?.seat_plural}
          </span>

          <Button
            variant="secondary"
            disabled={saving}
            onClick={() => setTarget(seats + 1)}
            title={texts?.add_seat}
          >
            <PlusIcon className="w-4 h-4" />
          </Button>
        </div>

        <Typography variant="txt_dim">
          {dirty
            ? `$${Math.round(unit * currentSeats * 100) / 100} → $${newTotal} ${periodLabel}`
            : `$${Math.round(total * 100) / 100} ${periodLabel}`}{" "}
          {currency !== "USD" ? currency : ""}
        </Typography>

        {dirty && (
          <div className="flex items-center gap-2 ml-auto">
            <Button 
              variant="secondary" 
              disabled={saving} 
              onClick={() => setTarget(null)}
            >
              {texts?.cancel}
            </Button>
            <Button
              variant="primary"
              disabled={saving}
              onClick={applyChange}
            >
              {saving
                ? texts?.updating
                : seats > currentSeats
                  ? t(texts?.purchase_seats_cta, { seatCount: seats - currentSeats })
                  : texts?.reduce_seats_cta}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
